import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

import { Heading } from '@/cms/Heading';
import { getStoreUrl } from '@/components/FindAStore/helpers';
import { LegacyButton } from '@/components/LegacyButton';
import type {
  ContentfulFindAStoreFragment,
  ContentfulHeadingFragment,
  ContentfulPlaceholderStoresFragment,
} from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import { useRegionProvider } from '@/providers/RegionProvider';

import { BoutiqueImage } from '../BoutiqueCard';

import * as S from './FindAStore.styles';

export interface BoutiqueDetail {
  cardType: string;
  info: ContentfulPlaceholderStoresFragment;
  noStoreResults?: boolean;
}

export const FindAStore = ({
  cardType,
  info,
  noStoreResults,
}: BoutiqueDetail) => {
  const { findAStore } = info;
  switch (findAStore?.type) {
    case 'card':
      if (cardType === 'card') {
        return (
          <PlaceholderStores
            {...findAStore}
            data-test-id='FindAStorePlaceholderStores'
          ></PlaceholderStores>
        );
      } else {
        return (
          <FindAStoreComponent
            {...(info.findAStore as ContentfulFindAStoreFragment)}
            data-test-id='FindAStoreFindAStoreComponent'
            noStoreResults={noStoreResults}
          ></FindAStoreComponent>
        );
      }
    case 'banner':
      return (
        <FindAStore.HasHeading
          cardType={cardType}
          data-test-id='FindAStoreHasHeading'
          info={info}
        ></FindAStore.HasHeading>
      );
    default:
      return <></>;
  }
};

export const FindAStoreHasHeading = ({ info }: BoutiqueDetail) => {
  const { placeholderHeading } = info;
  return (
    <>
      <Heading
        Heading={placeholderHeading as ContentfulHeadingFragment}
        data-test-id='FindAStoreHasHeadingHeading'
        sizeDesktop={50}
        sizeMobile={40}
      ></Heading>
      <FindAStoreComponent
        {...(info.findAStore as ContentfulFindAStoreFragment)}
        data-test-id='FindAStoreHasHeadingFindAStoreComponent'
      ></FindAStoreComponent>
    </>
  );
};

export const FindAStoreComponent = ({
  noStoreResults,
  ...info
}: ContentfulFindAStoreFragment & { noStoreResults?: boolean }) => {
  const { push } = useRouter();
  const { country, locale } = useRegionProvider();

  return (
    <>
      <S.FindAStore
        data-test-id='FindAStoreComponentFindAStore'
        findAStoreColor={info.findAStoreColor as string}
        flex={1}
      >
        <S.NoResultImage>
          <BoutiqueImage
            alt={info?.longImage?.title || ''}
            data-test-id='FindAStoreComponentBoutiqueImage'
            noStoreResults={noStoreResults}
            src={info?.longImage?.url || ''}
          ></BoutiqueImage>
        </S.NoResultImage>
        <S.NoResultInfoBox
          data-test-id='FindAStoreComponentNoResultInfoBox'
          flexDirection='column'
        >
          {info?.findAStoreText && (
            <S.NoResultBoutiqueInfo
              dangerouslySetInnerHTML={{
                __html: documentToHtmlString(info?.findAStoreText?.json),
              }}
              data-test-id='FindAStoreComponentNoResultBoutiqueInfo'
            ></S.NoResultBoutiqueInfo>
          )}
          <S.NoResultCTA>
            <S.NoResultButton
              data-testid='findAStore'
              onClick={() =>
                push(getStoreUrl(info?.url, locale.slug, country.code))
              }
            >
              {' '}
              {info?.urlText}{' '}
            </S.NoResultButton>
          </S.NoResultCTA>
        </S.NoResultInfoBox>
      </S.FindAStore>
    </>
  );
};

export const PlaceholderStores = (findAStore: ContentfulFindAStoreFragment) => {
  const { push } = useRouter();
  const { country, locale } = useRegionProvider();

  return (
    <>
      <S.PlaceholderStoresCard
        data-test-id='PlaceholderStoresPlaceholderStoresCard'
        findAStoreColor={findAStore.findAStoreColor as string}
        flex={1}
      >
        <S.PlaceholderStoresImage>
          <BoutiqueImage
            alt={findAStore?.cardImage?.title || ''}
            data-test-id='PlaceholderStoresBoutiqueImage'
            src={findAStore?.cardImage?.url || ''}
          ></BoutiqueImage>
        </S.PlaceholderStoresImage>
        <S.PlaceholderStoresInfoBox
          data-test-id='PlaceholderStoresInfoBox'
          flexDirection='column'
        >
          {findAStore?.findAStoreText && (
            <S.NoResultBoutiqueInfo
              dangerouslySetInnerHTML={{
                __html: documentToHtmlString(findAStore?.findAStoreText?.json),
              }}
              data-test-id='PlaceholderStoresNoResultBoutiqueInfo'
            ></S.NoResultBoutiqueInfo>
          )}
          <S.NoResultCTA>
            <LegacyButton
              data-testid='PlaceholderStoresNoResultButton'
              label={findAStore?.urlText}
              onClick={() =>
                push(getStoreUrl(findAStore?.url, locale.slug, country.code))
              }
              size='s'
              type='button'
              variant='filled'
              width='100%'
            />
          </S.NoResultCTA>
        </S.PlaceholderStoresInfoBox>
      </S.PlaceholderStoresCard>
    </>
  );
};
FindAStore.HasHeading = FindAStoreHasHeading;
FindAStore.displayName = 'FindAStore';
